import { useI18n } from '@platform/i18n';
import { Button, Dialog, DialogBody, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle, Input, MenuMultiSelect, MenuSelect, RecipientInput, Tabs, TabsContent, TabsList, TabsTrigger, useAsync } from '@platform/ui';
import { FileTextIcon, CalendarDaysIcon, UsersIcon, SlidersHorizontalIcon } from 'lucide-react';
import { useEffect, useId, useMemo, useRef, useState, type Ref } from 'react';
import { AccountDateTimePicker as DateTimePicker } from './date-time-picker';
import type { SearchDraft, SearchFilterDialogProps } from './search-filter-draft';
import { appendSearchPerson, searchDraftList, validateSearchDraft } from './search-filter-draft';
import { api } from './api';
import { useRecipientDirectory } from './recipient-directory';
import { useRecipientLabels } from './compose-labels';

type FieldProps<K extends keyof SearchDraft> = { field: K; label: string; value: SearchDraft[K]; change: SearchFilterDialogProps['onDraftChange']; placeholder?: string; inputRef?: Ref<HTMLInputElement> };

function TextField<K extends keyof SearchDraft>({ field, label, value, change, placeholder, inputRef }: FieldProps<K>) {
  return <label className="grid min-w-0 gap-1 text-sm">{label}<Input ref={inputRef} value={String(value)} placeholder={placeholder} onChange={(event) => change(field, event.target.value as SearchDraft[K])} /></label>;
}

export function SearchFilterDialog({ open, onOpenChange, draft, onDraftChange, errors, onApply, onClear, fallbackFocusRef }: SearchFilterDialogProps) {
  const { t } = useI18n();
  const directory = useRecipientDirectory();
  const searchDirectory = useMemo(() => ({ suggest: directory.suggest, resolve: () => Promise.resolve([]), photoUrl: directory.photoUrl }), [directory]);
  const { recipientLabels } = useRecipientLabels();
  const categories = useAsync(() => open ? api.calendarCategories() : Promise.resolve([]), String(open));
  const selectedCategories = searchDraftList(draft.category);
  const categoryNames = [...new Set([...(categories.data ?? []).map(category => category.name), ...selectedCategories])];
  const ids = { min: useId(), max: useId() };
  const subjectRef = useRef<HTMLInputElement>(null);
  const minRef = useRef<HTMLInputElement>(null);
  const maxRef = useRef<HTMLInputElement>(null);
  const [tab, setTab] = useState('content');
  useEffect(() => {
    if (tab !== 'properties') return;
    if (!errors.sizeMin && !errors.sizeMax) return;
    // Radix may mount the newly selected panel after this effect runs.
    const timer = window.setTimeout(() => (errors.sizeMin ? minRef.current : maxRef.current)?.focus(), 0);
    return () => window.clearTimeout(timer);
  }, [tab, errors.sizeMin, errors.sizeMax]);
  const wasOpen = useRef(false);
  useEffect(() => {
    if (open && !wasOpen.current) {
      setTab('content');
      const timer = window.setTimeout(() => subjectRef.current?.focus(), 0);
      wasOpen.current = open;
      return () => window.clearTimeout(timer);
    }
    wasOpen.current = open;
    return undefined;
  }, [open]);
  const field = <K extends keyof SearchDraft>(key: K, label: string, placeholder?: string) => <TextField key={key} field={key} label={label} value={draft[key]} change={onDraftChange} placeholder={placeholder} />;
  const peopleField = (key: 'from' | 'to' | 'cc') => <div className="grid min-w-0 gap-1 text-sm">
    {field(key, t(`webmail.searchAdvanced.${key}`), t('webmail.searchAdvanced.listPlaceholder'))}
    <RecipientInput field={`search-${key}`} label={t('webmail.searchAdvanced.peopleChoose', { field: t(`webmail.searchAdvanced.${key}`) })}
      placeholder={t('webmail.searchAdvanced.peopleChoose', { field: t(`webmail.searchAdvanced.${key}`) })}
      value={[]} onChange={values => {
        const selected = values.find(value => value.status === 'resolved' && value.email && value.key);
        if (selected) onDraftChange(key, appendSearchPerson(draft[key], selected.email));
      }} directory={searchDirectory} labels={recipientLabels} selectionOnly maxRecipients={1} />
  </div>;
  const select = (key: 'importance' | 'hasAttachments' | 'unread' | 'flagged', options: { value: string; label: string }[]) => (
    <div className="grid min-w-0 gap-1 text-sm">
      <span>{t(`webmail.searchAdvanced.${key}`)}</span>
      <MenuSelect label={t(`webmail.searchAdvanced.${key}`)} value={draft[key]} options={options} onChange={(value) => onDraftChange(key, value as SearchDraft[typeof key])} />
    </div>
  );
  const yesNo = [{ value: '', label: t('webmail.searchAdvanced.any') }, { value: 'yes', label: t('webmail.searchAdvanced.yes') }, { value: 'no', label: t('webmail.searchAdvanced.no') }];
  const sizeField = (key: 'sizeMin' | 'sizeMax', id: string, ref: typeof minRef) => {
    const error = errors[key];
    return <div className="grid min-w-0 gap-1 text-sm"><label htmlFor={id}>{t(`webmail.searchAdvanced.${key}`)}</label><div className="flex items-center gap-2"><Input ref={ref} id={id} type="text" inputMode="numeric" value={draft[key]} aria-invalid={Boolean(error)} aria-describedby={error ? `${id}-error` : undefined} onChange={(event) => onDraftChange(key, event.target.value)} /><span className="shrink-0 text-muted-foreground">{t('webmail.searchAdvanced.sizeBytes')}</span></div>{error && <p id={`${id}-error`} className="text-sm text-destructive">{t(error)}</p>}</div>;
  };
  const apply = () => {
    if (!onApply()) {
      changeTab('properties');
      const target = validateSearchDraft(draft).sizeMin ? minRef : maxRef;
      window.setTimeout(() => target.current?.focus(), 0);
    }
  };
  const changeTab = (next: string) => {
    setTab(next);
  };
  return <Dialog open={open} onOpenChange={onOpenChange}>
    <DialogContent framed size="lg" initialFocusRef={subjectRef} fallbackFocusRef={fallbackFocusRef} className="gap-3">
      <DialogHeader><DialogTitle>{t('webmail.searchAdvanced.open')}</DialogTitle><DialogDescription>{t('webmail.searchAdvanced.dialogDescription')}</DialogDescription></DialogHeader>
      <form className="flex min-h-0 flex-1 flex-col gap-3" onSubmit={(event) => { event.preventDefault(); apply(); }}>
      <Tabs value={tab} onValueChange={changeTab} className="min-h-0 flex-1">
        <TabsList aria-label={t('webmail.searchAdvanced.open')} className="w-full [&_span]:sm:not-sr-only">
          <TabsTrigger value="content" icon={FileTextIcon} label={t('webmail.searchAdvanced.tabContent')} />
          <TabsTrigger value="people" icon={UsersIcon} label={t('webmail.searchAdvanced.tabPeople')} />
          <TabsTrigger value="dates" icon={CalendarDaysIcon} label={t('webmail.searchAdvanced.tabDates')} />
          <TabsTrigger value="properties" icon={SlidersHorizontalIcon} label={t('webmail.searchAdvanced.tabProperties')} />
        </TabsList>
        <DialogBody className="pr-1">
          <TabsContent value="content" className="grid grid-cols-1 gap-3 sm:grid-cols-2">
            <TextField field="subject" label={t('webmail.searchAdvanced.subject')} value={draft.subject} change={onDraftChange} inputRef={subjectRef} />
            {field('body', t('webmail.searchAdvanced.body'))}
            {field('attachmentName', t('webmail.searchAdvanced.attachmentName'))}
            <div className="grid min-w-0 gap-2">
              {field('category', t('webmail.searchAdvanced.categories'), t('webmail.searchAdvanced.listPlaceholder'))}
              <MenuMultiSelect label={t('webmail.searchAdvanced.categoriesChoose')} values={selectedCategories}
                placeholder={t('webmail.searchAdvanced.any')}
                options={categoryNames.map(name => ({ value: name, label: name }))}
                onChange={values => onDraftChange('category', values.join(', '))} />
            </div>
          </TabsContent>
          <TabsContent value="people" className="grid grid-cols-1 gap-3 sm:grid-cols-2">
            <p className="text-xs text-muted-foreground sm:col-span-2">{t('webmail.searchAdvanced.peopleTextHint')}</p>
            {peopleField('from')}{peopleField('to')}{peopleField('cc')}
          </TabsContent>
          <TabsContent value="dates" className="grid grid-cols-1 gap-3 sm:grid-cols-2">
            <label className="grid gap-1 text-sm">{t('webmail.searchAdvanced.receivedFrom')}<DateTimePicker mode="date" value={draft.receivedFrom} onValueChange={(value) => onDraftChange('receivedFrom', value)} /></label>
            <label className="grid gap-1 text-sm">{t('webmail.searchAdvanced.receivedTo')}<DateTimePicker mode="date" value={draft.receivedTo} onValueChange={(value) => onDraftChange('receivedTo', value)} /></label>
          </TabsContent>
          <TabsContent value="properties" className="grid grid-cols-1 gap-3 sm:grid-cols-2">
            {sizeField('sizeMin', ids.min, minRef)}{sizeField('sizeMax', ids.max, maxRef)}
            {select('importance', [{ value: '', label: t('webmail.searchAdvanced.any') }, ...(['low', 'normal', 'high'] as const).map((value) => ({ value, label: t(`webmail.searchAdvanced.${value}`) }))])}
            {select('hasAttachments', yesNo)}{select('unread', yesNo)}{select('flagged', yesNo)}
          </TabsContent>
        </DialogBody>
      </Tabs>
      <DialogFooter><Button type="button" variant="outline" onClick={onClear}>{t('webmail.searchAdvanced.clear')}</Button><Button type="submit">{t('webmail.searchAdvanced.apply')}</Button></DialogFooter>
      </form>
    </DialogContent>
  </Dialog>;
}
